import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  ExternalLink,
  Volume2,
  VolumeX,
  MoreHorizontal,
  Settings,
  Pause,
  Play,
  Zap } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from
'@/components/ui/dropdown-menu';
import SlideRenderer from './SlideRenderer';
import { motion, AnimatePresence } from 'motion/react';

interface ProcessedSlide {
  id: string;
  title: string;
  content: string | string[];
  layoutType: string;
  processedContent: any;
  designApplied: boolean;
  type: 'title' | 'content' | 'image' | 'list' | 'conclusion' | 'quote' | 'comparison' | 'image-text';
  notes?: string;
  imageUrl?: string;
  imageAlt?: string;
}

interface Template {
  id: string;
  name: string;
  preview: {
    titleSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    contentSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  colorScheme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

interface FullscreenPresentationViewProps {
  slides: ProcessedSlide[];
  template: Template;
  title: string;
  onClose: () => void;
  autoplay?: boolean;
  autoplayDelay?: number;
}

const FullscreenPresentationView: React.FC<FullscreenPresentationViewProps> = ({
  slides,
  template,
  title,
  onClose,
  autoplay = false,
  autoplayDelay = 5000
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [showControls, setShowControls] = useState(true);
  const [mouseTimeout, setMouseTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isAudioMuted, setIsAudioMuted] = useState(true);

  const currentSlide = slides[currentSlideIndex];
  const progressPercentage = (currentSlideIndex + 1) / slides.length * 100;

  // Navigation functions
  const nextSlide = useCallback(() => {
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    } else if (isPlaying) {
      // Loop back to beginning if autoplay is on
      setCurrentSlideIndex(0);
    }
  }, [currentSlideIndex, slides.length, isPlaying]);

  const prevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  }, [currentSlideIndex]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlideIndex(index);
  }, []);

  // Keyboard navigation
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowRight':
      case ' ':
      case 'PageDown':
        e.preventDefault();
        nextSlide();
        break;
      case 'ArrowLeft':
      case 'PageUp':
        e.preventDefault();
        prevSlide();
        break;
      case 'Home':
        e.preventDefault();
        goToSlide(0);
        break;
      case 'End':
        e.preventDefault();
        goToSlide(slides.length - 1);
        break;
      case 'Escape':
      case 'F11':
        e.preventDefault();
        onClose();
        break;
      case 'p':
      case 'P':
        e.preventDefault();
        setIsPlaying(!isPlaying);
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        setIsAudioMuted(!isAudioMuted);
        break;
      default:
        // Number keys for direct slide navigation
        const num = parseInt(e.key);
        if (num >= 1 && num <= Math.min(9, slides.length)) {
          e.preventDefault();
          goToSlide(num - 1);
        }
        break;
    }
  }, [nextSlide, prevSlide, goToSlide, slides.length, onClose, isPlaying, isAudioMuted]);

  // Mouse movement handling for auto-hide controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (mouseTimeout) {
      clearTimeout(mouseTimeout);
    }
    const timeout = setTimeout(() => {
      setShowControls(false);
    }, 3000);
    setMouseTimeout(timeout);
  }, [mouseTimeout]);

  // Autoplay functionality
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isPlaying && slides.length > 1) {
      interval = setInterval(() => {
        nextSlide();
      }, autoplayDelay);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, nextSlide, autoplayDelay, slides.length]);

  // Event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    document.addEventListener('mousemove', handleMouseMove);

    // Initial hide timer
    const initialTimeout = setTimeout(() => {
      setShowControls(false);
    }, 3000);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('mousemove', handleMouseMove);
      if (mouseTimeout) clearTimeout(mouseTimeout);
      clearTimeout(initialTimeout);
    };
  }, [handleKeyPress, handleMouseMove, mouseTimeout]);

  // Fullscreen API handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const renderSlideContent = (slide: ProcessedSlide) => {
    // Create a normalized slide object for SlideRenderer
    const normalizedSlide = {
      id: slide.id,
      title: slide.title,
      content: Array.isArray(slide.content) ? slide.content.join('\n') : slide.content || '',
      type: slide.type || 'content',
      notes: slide.notes,
      imageUrl: slide.imageUrl,
      imageAlt: slide.imageAlt,
      template: template
    };

    return (
      <SlideRenderer
        slide={normalizedSlide}
        template={template}
        isEditing={false} data-id="ag70s9trj" data-path="src/components/ai/FullscreenPresentationView.tsx" />);


  };

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col overflow-hidden" data-id="sy60veg4d" data-path="src/components/ai/FullscreenPresentationView.tsx">
      {/* Top Controls Bar */}
      <AnimatePresence data-id="yv5fdl311" data-path="src/components/ai/FullscreenPresentationView.tsx">
        {showControls &&
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-6" data-id="gikbr52w8" data-path="src/components/ai/FullscreenPresentationView.tsx">

            <div className="flex items-center justify-between text-white" data-id="yiw1v01bb" data-path="src/components/ai/FullscreenPresentationView.tsx">
              <div className="flex items-center gap-4" data-id="gex3r65zi" data-path="src/components/ai/FullscreenPresentationView.tsx">
                <h1 className="text-xl font-bold truncate max-w-md" data-id="ha1inixih" data-path="src/components/ai/FullscreenPresentationView.tsx">{title}</h1>
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30" data-id="t2t7i5njg" data-path="src/components/ai/FullscreenPresentationView.tsx">
                  {currentSlideIndex + 1} / {slides.length}
                </Badge>
                {template.name &&
              <Badge variant="outline" className="border-white/30 text-white" data-id="20zsfyj8s" data-path="src/components/ai/FullscreenPresentationView.tsx">
                    {template.name}
                  </Badge>
              }
              </div>
              
              <div className="flex items-center gap-2" data-id="rgpcmbj2n" data-path="src/components/ai/FullscreenPresentationView.tsx">
                <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPlaying(!isPlaying)}
                className="text-white hover:bg-white/20" data-id="s9wdp1sqc" data-path="src/components/ai/FullscreenPresentationView.tsx">

                  {isPlaying ?
                <Pause className="w-4 h-4" data-id="z6zc652f8" data-path="src/components/ai/FullscreenPresentationView.tsx" /> :

                <Play className="w-4 h-4" data-id="j2dvumgh8" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                }
                </Button>
                
                <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAudioMuted(!isAudioMuted)}
                className="text-white hover:bg-white/20" data-id="wuybvf8uy" data-path="src/components/ai/FullscreenPresentationView.tsx">

                  {isAudioMuted ?
                <VolumeX className="w-4 h-4" data-id="u3jj6ss9n" data-path="src/components/ai/FullscreenPresentationView.tsx" /> :

                <Volume2 className="w-4 h-4" data-id="cpi6tt8n9" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                }
                </Button>

                <DropdownMenu data-id="dmrece6ja" data-path="src/components/ai/FullscreenPresentationView.tsx">
                  <DropdownMenuTrigger asChild data-id="6rwhq6v4q" data-path="src/components/ai/FullscreenPresentationView.tsx">
                    <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20" data-id="2iu20ytdx" data-path="src/components/ai/FullscreenPresentationView.tsx">

                      <MoreHorizontal className="w-4 h-4" data-id="b4szjzcgd" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-black/90 text-white border-white/20" data-id="50tjepepo" data-path="src/components/ai/FullscreenPresentationView.tsx">
                    <DropdownMenuItem onClick={toggleFullscreen} className="hover:bg-white/20" data-id="p82hbp4mn" data-path="src/components/ai/FullscreenPresentationView.tsx">
                      <Maximize2 className="w-4 h-4 mr-2" data-id="lvlg25rou" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                      Toggle Fullscreen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => goToSlide(0)} className="hover:bg-white/20" data-id="izzabwimr" data-path="src/components/ai/FullscreenPresentationView.tsx">
                      <Zap className="w-4 h-4 mr-2" data-id="twtdd9x9s" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                      Go to Start
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/20" data-id="gnxfd0ms8" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                    <DropdownMenuItem onClick={onClose} className="hover:bg-white/20" data-id="j1dso8lxf" data-path="src/components/ai/FullscreenPresentationView.tsx">
                      <ExternalLink className="w-4 h-4 mr-2" data-id="7ai8ny48u" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                      Exit Presentation
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20" data-id="rcbnb18md" data-path="src/components/ai/FullscreenPresentationView.tsx">

                  <ExternalLink className="w-4 h-4" data-id="l3y7isawr" data-path="src/components/ai/FullscreenPresentationView.tsx" />
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4" data-id="ylropax67" data-path="src/components/ai/FullscreenPresentationView.tsx">
              <Progress
              value={progressPercentage}
              className="h-1 bg-white/20" data-id="cm73k3785" data-path="src/components/ai/FullscreenPresentationView.tsx" />

            </div>
          </motion.div>
        }
      </AnimatePresence>

      {/* Main Slide Content */}
      <div className="flex-1 flex items-center justify-center p-8 relative" data-id="70asx1pw9" data-path="src/components/ai/FullscreenPresentationView.tsx">
        <motion.div
          key={currentSlideIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="w-full max-w-7xl bg-white rounded-lg shadow-2xl overflow-hidden"
          style={{ aspectRatio: '16/9' }} data-id="51h4xeg9i" data-path="src/components/ai/FullscreenPresentationView.tsx">

          {renderSlideContent(currentSlide)}
        </motion.div>
      </div>

      {/* Bottom Controls Bar */}
      <AnimatePresence data-id="3iyas1rip" data-path="src/components/ai/FullscreenPresentationView.tsx">
        {showControls &&
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/80 to-transparent p-6" data-id="yta345t6t" data-path="src/components/ai/FullscreenPresentationView.tsx">

            <div className="flex items-center justify-center gap-4" data-id="vmd26s4as" data-path="src/components/ai/FullscreenPresentationView.tsx">
              <Button
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              disabled={currentSlideIndex === 0}
              className="text-white hover:bg-white/20 disabled:opacity-30" data-id="9txptnn8a" data-path="src/components/ai/FullscreenPresentationView.tsx">

                <ChevronLeft className="w-5 h-5" data-id="mbe7e2z7l" data-path="src/components/ai/FullscreenPresentationView.tsx" />
              </Button>

              <div className="flex items-center gap-2 px-4" data-id="kygk9yy2p" data-path="src/components/ai/FullscreenPresentationView.tsx">
                {slides.map((_, index) =>
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentSlideIndex ?
                'bg-white scale-125' :
                'bg-white/40 hover:bg-white/60'}`
                } data-id="mc64smui5" data-path="src/components/ai/FullscreenPresentationView.tsx" />

              )}
              </div>

              <Button
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              disabled={currentSlideIndex === slides.length - 1 && !isPlaying}
              className="text-white hover:bg-white/20 disabled:opacity-30" data-id="9s94cyfzy" data-path="src/components/ai/FullscreenPresentationView.tsx">

                <ChevronRight className="w-5 h-5" data-id="wmcmmnm29" data-path="src/components/ai/FullscreenPresentationView.tsx" />
              </Button>
            </div>

            {/* Slide Navigation Thumbnails */}
            <div className="mt-4 flex items-center justify-center gap-2 overflow-x-auto pb-2" data-id="2w3jwnftf" data-path="src/components/ai/FullscreenPresentationView.tsx">
              {slides.map((slide, index) =>
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-16 h-10 rounded border-2 transition-all duration-200 ${
              index === currentSlideIndex ?
              'border-white bg-white/20' :
              'border-white/30 bg-white/10 hover:border-white/50'}`
              } data-id="qqmurrc9c" data-path="src/components/ai/FullscreenPresentationView.tsx">

                  <div className="w-full h-full flex items-center justify-center text-xs text-white font-medium" data-id="026lxb5v4" data-path="src/components/ai/FullscreenPresentationView.tsx">
                    {index + 1}
                  </div>
                </button>
            )}
            </div>
          </motion.div>
        }
      </AnimatePresence>

      {/* Help Text Overlay */}
      <AnimatePresence data-id="wtc1np8y3" data-path="src/components/ai/FullscreenPresentationView.tsx">
        {showControls &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute bottom-24 left-6 text-white/60 text-sm" data-id="14brj787j" data-path="src/components/ai/FullscreenPresentationView.tsx">

            <p data-id="wdgnsoncc" data-path="src/components/ai/FullscreenPresentationView.tsx">Use arrow keys, space, or click to navigate • Press ESC to exit • P to pause • M to mute</p>
          </motion.div>
        }
      </AnimatePresence>
    </div>);

};

export default FullscreenPresentationView;